import { LifeBuoy, Plus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { PageHeader } from '@/components/common/PageHeader'
import { EmptyState, ErrorState } from '@/components/common/States'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { confirmModal, Modal } from '@/components/ui/modal'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table'
import { apiErrorMessage } from '@/i18n'
import { useAuth } from '@/lib/auth/AuthProvider'
import { cn } from '@/lib/utils'
import { fmtDateTime } from '../shared/format'
import type { Ticket } from '../shared/types'
import { useCreateTicket, useTickets, useUpdateTicket } from './api'
import { MessagesPanel } from './MessagesPanel'

const STATUSES = ['open', 'in_progress', 'resolved', 'closed'] as const

export function TicketsPage() {
  const { t } = useTranslation()
  const { can } = useAuth()
  const tickets = useTickets()
  const create = useCreateTicket()
  const update = useUpdateTicket()
  const [createOpen, setCreateOpen] = useState(false)
  const [selected, setSelected] = useState<Ticket | null>(null)
  const [accountId, setAccountId] = useState('')
  const [title, setTitle] = useState('')
  const [moduleCode, setModuleCode] = useState('')

  const onCreate = async () => {
    try {
      await create.mutateAsync({ account_id: accountId, title, module_code: moduleCode || undefined })
      toast.success(t('common.saved'))
      setCreateOpen(false)
      setAccountId('')
      setTitle('')
      setModuleCode('')
    } catch (e) {
      toast.error(apiErrorMessage(e))
    }
  }

  const onStatus = async (ticket: Ticket, status: string) => {
    const label = (s: string) => t(`tickets.status.${s}`, { defaultValue: s })
    const ok = await confirmModal({
      title: t('confirm.ticketStatus.title'),
      description: t('confirm.ticketStatus.body', { title: ticket.title, from: label(ticket.status), to: label(status) }),
      confirmLabel: t('common.confirm'),
    })
    if (!ok) return
    try {
      await update.mutateAsync({ id: ticket.id, status })
      toast.success(t('common.saved'))
      if (selected?.id === ticket.id) setSelected({ ...ticket, status })
    } catch (e) {
      toast.error(apiErrorMessage(e))
    }
  }

  const rows = tickets.data ?? []

  return (
    <>
      <PageHeader
        title={t('tickets.title')}
        description={t('tickets.subtitle')}
        actions={
          can('tickets.write') ? (
            <Button onClick={() => setCreateOpen(true)}>
              <Plus />
              {t('tickets.new')}
            </Button>
          ) : null
        }
      />
      <div className="grid grid-cols-[minmax(0,1fr)] gap-4 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <Card>
          {tickets.isError ? (
            <ErrorState error={tickets.error} onRetry={() => void tickets.refetch()} />
          ) : (
            <Table>
              <THead>
                <TR className="hover:bg-transparent">
                  <TH>{t('tickets.columns.title')}</TH>
                  <TH className="hidden md:table-cell">{t('tickets.columns.module')}</TH>
                  <TH>{t('tickets.columns.status')}</TH>
                  <TH className="hidden sm:table-cell text-right">{t('tickets.columns.created')}</TH>
                </TR>
              </THead>
              <TBody>
                {tickets.isLoading
                  ? Array.from({ length: 6 }, (_, i) => (
                      <TR key={i}>
                        <TD colSpan={4}>
                          <Skeleton className="h-6" />
                        </TD>
                      </TR>
                    ))
                  : rows.map((row) => (
                      <TR
                        key={row.id}
                        className={cn('cursor-pointer', selected?.id === row.id && 'bg-muted/50')}
                        onClick={() => setSelected(row)}
                      >
                        <TD className="font-medium">{row.title || row.subject}</TD>
                        <TD className="hidden md:table-cell text-muted-foreground">{row.module_code || '—'}</TD>
                        <TD>
                          {can('tickets.write') ? (
                            <div onClick={(e) => e.stopPropagation()}>
                              <Select
                                size="sm"
                                className="w-auto min-w-32"
                                value={row.status}
                                onChange={(v) => v !== row.status && void onStatus(row, v)}
                                options={STATUSES.map((s) => ({ value: s, label: t(`tickets.status.${s}`, { defaultValue: s }) }))}
                                aria-label={t('tickets.columns.status')}
                              />
                            </div>
                          ) : (
                            <Badge tone="outline">{row.status}</Badge>
                          )}
                        </TD>
                        <TD className="hidden sm:table-cell text-right text-muted-foreground tabular-nums">
                          {fmtDateTime(row.created_at)}
                        </TD>
                      </TR>
                    ))}
              </TBody>
            </Table>
          )}
          {!tickets.isLoading && !tickets.isError && rows.length === 0 ? <EmptyState /> : null}
        </Card>
        <Card className="min-h-80 overflow-hidden">
          {selected ? (
            <MessagesPanel ticketId={selected.id} accountId={selected.account_id} />
          ) : (
            <EmptyState title={t('tickets.pickTicket')} />
          )}
        </Card>
      </div>

      <Modal
        open={createOpen}
        onOpenChange={setCreateOpen}
        icon={<LifeBuoy />}
        title={t('tickets.new')}
        description={t('tickets.formHint')}
        footer={
          <>
            <Button type="button" variant="cancel" onClick={() => setCreateOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="button" disabled={create.isPending || !accountId || !title} onClick={() => void onCreate()}>
              {t('common.save')}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor="account">{t('tickets.columns.account')}</Label>
            <Input id="account" value={accountId} onChange={(e) => setAccountId(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="title">{t('tickets.columns.title')}</Label>
            <Input id="title" value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="module">{t('tickets.columns.module')}</Label>
            <Input id="module" value={moduleCode} onChange={(e) => setModuleCode(e.target.value)} />
          </div>
        </div>
      </Modal>
    </>
  )
}
